import { DateFormatNL } from "../constants";

/**
 * Formats a date string into a human-readable format based on the specified locale.
 *
 * @param dateString - The date string to format.
 * @param locale - The locale to use for formatting the date. Defaults to "nl-NL".
 * @param format - How to format the month. Defaults to "long".
 * @returns The formatted date string. Returns an empty string if the input date string is invalid.
 */
export const formatDate = (
  dateString: string,
  locale: string = DateFormatNL,
  format: string = "long"
): string => {
  if (!dateString) return "";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return "";
  }

  const options: Intl.DateTimeFormatOptions =
    format === "long"
      ? { day: "numeric", month: "long", year: "numeric" }
      : { day: "numeric", month: "short" };

  return new Intl.DateTimeFormat(locale, options).format(date);
};

export type TimeUnit = "days" | "hours" | "minutes";

export const TIME_UNITS = {
  DAYS: "days",
  HOURS: "hours",
  MINUTES: "minutes",
} as const;

/**
 * Gets the time since a given timestamp.
 *
 * @param timestamp - The timestamp to compare against.
 * @param options - Options for the function.
 * @returns - a number representing the time unit since the notification was created
 */
export function getTimeSince(
  timestamp: number | null,
  options: { units: TimeUnit[] }
): Record<TimeUnit, number> {
  const timeStampInSeconds = 10000000000;
  // guard clause
  if (!timestamp) {
    return { days: 0, hours: 0, minutes: 0 };
  }

  // If timestamp is in seconds
  if (timestamp < timeStampInSeconds) {
    // Convert to milliseconds
    timestamp = timestamp * 1000;
  }

  const now = new Date();
  const time = new Date(timestamp);

  // Return 0 if the timestamp is in the future
  if (time > now) {
    return { days: 0, hours: 0, minutes: 0 };
  }

  const diff = now.getTime() - time.getTime();
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);

  const result: Record<string, number> = {};
  for (const unit of options.units) {
    if (unit === TIME_UNITS.DAYS) result.days = days;
    if (unit === TIME_UNITS.HOURS) result.hours = hours;
    if (unit === TIME_UNITS.MINUTES) result.minutes = minutes;
  }
  return result;
}
